import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "mandjaro_cookies_accepted_v1";
const COOKIE_NAME = "mandjaro_cookies_accepted";
// Durée de validité du consentement : 12 mois (recommandation CNIL)
const CONSENT_MAX_AGE_DAYS = 365;

function readConsent(): boolean {
  // 1) localStorage (persistant tant que l'utilisateur ne vide pas son navigateur)
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v) {
      const ts = parseInt(v, 10);
      if (!Number.isNaN(ts)) {
        const ageDays = (Date.now() - ts) / 86_400_000;
        if (ageDays < CONSENT_MAX_AGE_DAYS) return true;
      } else if (v === "1") {
        // ancien format → on considère valide et on remettra à jour à l'écriture
        return true;
      }
    }
  } catch {
    /* mode privé : ignore */
  }

  // 2) Fallback cookie (survit à un vidage du localStorage uniquement)
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]+)`)
    );
    if (match) return true;
  } catch {
    /* noop */
  }

  return false;
}

function writeConsent() {
  const ts = Date.now().toString();
  try {
    window.localStorage.setItem(STORAGE_KEY, ts);
  } catch {
    /* noop */
  }
  try {
    const maxAge = CONSENT_MAX_AGE_DAYS * 24 * 60 * 60;
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${COOKIE_NAME}=1; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* noop */
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!readConsent()) setVisible(true);
  }, []);

  if (!visible) return null;

  const accept = () => {
    writeConsent();
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gold/40 shadow-lg"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <Cookie size={20} className="text-gold shrink-0" aria-hidden />
        <p
          id="cookie-banner-title"
          className="text-xs sm:text-sm text-foreground flex-1 leading-relaxed"
        >
          Ce site utilise des cookies techniques nécessaires à son fonctionnement. En
          continuant, vous acceptez leur utilisation.{" "}
          <Link
            to="/confidentialite"
            className="underline text-foreground/80 hover:text-foreground"
          >
            En savoir plus
          </Link>
        </p>
        <Button
          onClick={accept}
          className="bg-gold text-black hover:bg-gold/90 text-xs sm:text-sm h-9 px-4 shrink-0 self-stretch sm:self-auto"
        >
          J'accepte
        </Button>
      </div>
    </div>
  );
}
